// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { Locale } from "./I18nContext"

/**
 * Metric/EUR formatting at the client boundary (per T09 + AGENTS.md:
 * "canonical metric units stored server-side" — conversion/localization
 * happens only here, at render time, never server-side or in storage).
 */

function intlLocale(locale: Locale): string {
  // "en-DE" keeps German conventions (comma decimals not applicable to
  // EUR display in en, but grouping/date order matches a Germany-first
  // product) while still rendering English words.
  return locale === "de" ? "de-DE" : "en-DE"
}

/** Formats an integer amount of cents as a localized EUR string, e.g. "3,80 €" / "€3.80". */
export function formatEurFromCents(amountCents: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "currency",
    currency: "EUR",
  }).format(amountCents / 100)
}

/** Formats a gram quantity, switching to kilograms above 1000 g. */
export function formatMetricWeightGrams(grams: number, locale: Locale): string {
  if (Math.abs(grams) >= 1000) {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: "unit",
      unit: "kilogram",
      unitDisplay: "short",
      maximumFractionDigits: 1,
    }).format(grams / 1000)
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "unit",
    unit: "gram",
    unitDisplay: "short",
    maximumFractionDigits: 0,
  }).format(grams)
}

/** Formats a millilitre quantity, switching to litres above 1000 ml. */
export function formatMetricVolumeMl(milliliters: number, locale: Locale): string {
  if (Math.abs(milliliters) >= 1000) {
    return new Intl.NumberFormat(intlLocale(locale), {
      style: "unit",
      unit: "liter",
      unitDisplay: "short",
      maximumFractionDigits: 1,
    }).format(milliliters / 1000)
  }
  return new Intl.NumberFormat(intlLocale(locale), {
    style: "unit",
    unit: "milliliter",
    unitDisplay: "short",
    maximumFractionDigits: 0,
  }).format(milliliters)
}

/** Formats a whole number with locale grouping and NO unit (for copy templates that carry their own "kcal"). */
export function formatWholeNumber(value: number, locale: Locale): string {
  return new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(value)
}

/** Formats whole kilocalories, e.g. "650 kcal". */
export function formatKcal(kcal: number, locale: Locale): string {
  return `${formatWholeNumber(kcal, locale)} kcal`
}

/**
 * Formats a fractional serving count (1 / 1.5 / 2) with the locale's decimal
 * separator: "1,5" (de) / "1.5" (en). Plain "en" here, not "en-DE": a
 * serving count is a small fraction, not a grouped quantity, and "1,5
 * servings" reads as a typo in English.
 */
export function formatServings(servings: number, locale: Locale): string {
  return new Intl.NumberFormat(locale === "de" ? "de-DE" : "en", { maximumFractionDigits: 1 }).format(
    servings
  )
}

/** Formats minutes as a compact duration, e.g. "25 min". */
export function formatMinutes(minutes: number, locale: Locale): string {
  return `${new Intl.NumberFormat(intlLocale(locale), { maximumFractionDigits: 0 }).format(minutes)} min`
}

/**
 * Compact day+month for offer validity windows, e.g. "29.08." (de) /
 * "29/08" (en-DE). ISO date-only strings are pinned to UTC so the rendered
 * day can never drift across the user's timezone.
 */
export function formatShortDate(isoDate: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: "2-digit",
    month: "2-digit",
    timeZone: "UTC",
  }).format(new Date(isoDate))
}

/** Full numeric date (day.month.year) for offer capture/provenance lines, pinned to UTC like formatShortDate. */
export function formatFullDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(iso))
}
